import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Grid,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SidebarLayout from '../components/SidebarLayout';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Pagination from '@mui/material/Pagination';

interface Record {
  id: number;
  client_id: string;
  status: number;
  execution_time: string;
  content: string;
  user?: { username: string }; // 新增
}

interface Statistics {
  total: number;
  success_count: number;
  failed_count: number;
}

interface User {
  id: number;
  username: string;
}

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [records, setRecords] = useState<Record[]>([]);
  const [allClients, setAllClients] = useState<{ client_id: string; hostname: string; user_id: number }[]>([]); // 全部客户端
  const [clients, setClients] = useState<{ client_id: string; hostname: string }[]>([]); // 当前下拉用
  const [users, setUsers] = useState<User[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<number | ''>('');
  const [recordStats, setRecordStats] = useState<Statistics>({
    total: 0,
    success_count: 0,
    failed_count: 0,
  });
  const [commentStats, setCommentStats] = useState<Statistics>({
    total: 0,
    success_count: 0,
    failed_count: 0,
  });
  const [userSearch, setUserSearch] = useState('');
  const [status, setStatus] = useState<string>('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');

  // 获取所有用户和所有客户端
  useEffect(() => {
    const fetchInit = async () => {
      try {
        // 获取所有客户端
        const clientsResponse = await axios.get('/api/clients');
        setAllClients(clientsResponse.data);
        // 管理员获取用户列表
        if (user?.role === 'admin') {
          const usersResponse = await axios.get('/api/users');
          setUsers(usersResponse.data.users);
        }
      } catch (error) {
        console.error('初始化数据失败:', error);
      }
    };
    fetchInit();
  }, [user]);

  // 根据选中用户过滤客户端
  useEffect(() => {
    let filterUserId: number | undefined;
    if (user?.role === 'admin') {
      filterUserId = selectedUser === '' ? undefined : Number(selectedUser);
    } else {
      filterUserId = user?.id;
    }
    const filtered = filterUserId
      ? allClients.filter(c => c.user_id === filterUserId)
      : allClients;
    setClients(filtered.map(c => ({ client_id: c.client_id, hostname: c.hostname })));
    // 如果当前选中的客户端不在新列表里，重置
    if (selectedClient && !filtered.some(c => c.client_id === selectedClient)) {
      setSelectedClient('');
    }
  }, [allClients, selectedUser, user, selectedClient]);

  // 查询数据
  useEffect(() => {
    fetchData();
  }, [selectedClient, selectedUser, user, status, page, limit, startTime, endTime]);

  const fetchData = async () => {
    try {
      let userId: number | undefined;
      if (user?.role === 'admin') {
        userId = selectedUser === '' ? undefined : Number(selectedUser);
      } else {
        userId = user?.id;
      }
      const params = {
        clientId: selectedClient || undefined,
        userId: userId || undefined,
        status,
        page,
        limit,
        startTime: startTime || undefined,
        endTime: endTime || undefined
      };
      const recordsResponse = await axios.get('/api/records', { params });
      setRecords(recordsResponse.data.records);
      setTotal(recordsResponse.data.total || 0);
      const statsResponse = await axios.get('/api/records/statistics', { params });
      setRecordStats(statsResponse.data);
    } catch (error) {
      console.error('获取数据失败:', error);
    }
  };

  const fetchUsers = async (search = '') => {
    const usersResponse = await axios.get('/api/users', { params: { search } });
    setUsers(usersResponse.data.users);
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchUsers();
    }
  }, [user]);

  const renderStatistics = (stats: Statistics) => (
    <>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography color='textSecondary' gutterBottom>
              总执行次数
            </Typography>
            <Typography variant='h5'>
              {stats.total}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography color='textSecondary' gutterBottom>
              成功次数
            </Typography>
            <Typography variant='h5' color='primary'>
              {stats.success_count}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography color='textSecondary' gutterBottom>
              失败次数
            </Typography>
            <Typography variant='h5' color='error'>
              {stats.failed_count}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </>
  );

  const renderTable = (data: Record[]) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>时间</TableCell>
            <TableCell>状态</TableCell>
            <TableCell>内容</TableCell>
            <TableCell>操作人</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((record) => (
            <TableRow key={record.id}>
              <TableCell>
                {new Date(record.execution_time).toLocaleString()}
              </TableCell>
              <TableCell>
                <Typography color={record.status === 1 ? 'success.main' : 'error.main'}>
                  {record.status === 1 ? '成功' : '失败'}
                </Typography>
              </TableCell>
              <TableCell>{record.content}</TableCell>
              <TableCell>{record.user?.username}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <SidebarLayout>
      <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={2}>
          {/* 统计卡片 */}
          {renderStatistics(recordStats)}
          {/* 筛选器 */}
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="center">
              {user?.role === 'admin' && (
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={users}
                    getOptionLabel={(option) => option.username}
                    value={users.find(u => u.id === selectedUser) || null}
                    onChange={(_, newValue) => setSelectedUser(newValue ? newValue.id : '')}
                    inputValue={userSearch}
                    onInputChange={(_, newInputValue) => {
                      setUserSearch(newInputValue);
                      fetchUsers(newInputValue);
                    }}
                    renderInput={(params) => <TextField {...params} label="选择用户" variant="outlined" fullWidth />}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    clearOnBlur={false}
                    sx={{ width: '100%' }}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>状态</InputLabel>
                  <Select
                    value={status}
                    label="状态"
                    onChange={e => {
                      setStatus(e.target.value);
                      setPage(1);
                    }}
                  >
                    <MenuItem value="">全部</MenuItem>
                    <MenuItem value="1">成功</MenuItem>
                    <MenuItem value="0">失败</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="开始时间"
                  type="datetime-local"
                  value={startTime}
                  onChange={e => setStartTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="结束时间"
                  type="datetime-local"
                  value={endTime}
                  onChange={e => setEndTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {renderTable(records)}
          </Grid>
          <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
            <Pagination
              count={Math.ceil(total / limit)}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              总条数: {total}
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </SidebarLayout>
  );
};

export default Dashboard; 